/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Address, StreamOnProduct, SubmitFullOrderInput, UserDetails } from '@mfe/shared/schema-types';

import { ErrorsObj, RuntimeError } from '../graphql';
import { ConfigureStream, Pages, Persona, StreamOnPromoState } from './types';

type ErrorPayload = RuntimeError | ErrorsObj | undefined | null;

const initialState: StreamOnPromoState = {
  shippingAddress: {
    addressLines: [''],
    municipality: '',
    region: '',
    postalCode: '',
    countryCode: '',
  },
  offerDetails: {
    loading: null,
    attributes: [],
    description: '',
    discountValue: 0,
    name: '',
    price: 0,
    offerId: '',
    buttonText: '',
    hyperlinkText: '',
    hyperlinkUrl: '',
    imageUrl: '',
    logoURL: '',
    title: '',
    tag: '',
    productTypeId: '',
    productFamily: '',
  },
  userDetails: {
    phoneNumber: '',
    email: '',
    error: {
      failureReason: '',
    },
  },
  streamEligibility: {
    error: null,
    isEligible: null,
  },
  configureStreamSuccess: false,
  configureStreamError: null,
  configureStreamLoading: true,
  configureStream: {
    productTypeId: '',
    rootProductTypeId: '',
    shoppingCartId: '',
  },
  isLoading: false,
  isOrderSubmitting: false,
  isScrubbingLoading: false,
  isEditingNumber: false,
  phoneNumber: '',
  scrubLocation: {
    addressLines: [''],
    municipality: '',
    region: '',
    postalCode: '',
    countryCode: '',
  },
  confirmModal: false,
  isEditingAddress: false,
  editAddress: {
    addressLines: ['', ''],
    municipality: '',
    region: '',
    postalCode: '',
    countryCode: '',
  },
  currentMonthlyBill: {
    loading: null,
    currentMonthlyBill: 0,
    error: { failureReason: '' },
  },
  currentPage: Pages.login,
  persona: Persona.CUSTOMER,
  didOrderFail: {
    didOrderFail: false,
    failureReason: '',
  },
  didOrderSucceed: false,
  orderAttempts: 0,
};

export const streamOnPromoSlice = createSlice({
  name: 'streamOnPromo',
  initialState,
  reducers: {
    getStreamEligibility: (state) => state,
    setStreamEligibility(state, { payload }: { payload: { isEligible: boolean } }) {
      state.streamEligibility.isEligible = payload.isEligible;
    },
    setStreamError: (state, action: PayloadAction<SetErrorActionPayload>) => {
      state.streamEligibility.error = action.payload;
    },
    getInvoiceHistory: (state) => state,
    getAllDetails: (state) => state,
    submitFullOrder: (state, _: PayloadAction<SubmitFullOrderInput>) => state,
    scrubAddress: (state, _: PayloadAction<{ address: Address | null }>) => state,
    setShippingAddress(state, action: PayloadAction<Address>) {
      const { __typename, ...restOfAddress } = action.payload;
      state.shippingAddress = restOfAddress;
    },
    setOfferDetailsLoading(state, action: PayloadAction<boolean>) {
      state.offerDetails.loading = action.payload;
    },
    setOfferDetails(state, action: PayloadAction<StreamOnProduct>) {
      state.offerDetails = {
        loading: false,
        ...action.payload,
      };
    },
    setConfigureStream: (state, action: PayloadAction<ConfigureStream>) => {
      state.configureStreamLoading = true;
      state.configureStream = action.payload;
    },
    setConfigureStreamSuccess(state, action: PayloadAction<boolean>) {
      state.configureStreamSuccess = action.payload;
      state.configureStreamLoading = false;
      state.configureStreamError = null;
    },
    setConfigureStreamError(state, action: PayloadAction<ErrorPayload>) {
      state.configureStreamLoading = false;
      state.configureStreamSuccess = false;
      state.configureStreamError = action.payload;
    },

    setUserDetails(state, action: PayloadAction<UserDetails | null | undefined>) {
      if (!action.payload) {
        state.userDetails = { ...initialState.userDetails };
      } else {
        state.userDetails = action.payload;
      }
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setIsOrderSubmitting(state, action: PayloadAction<boolean>) {
      state.isOrderSubmitting = action.payload;
    },
    setIsScrubbingLoading(state, action: PayloadAction<boolean>) {
      state.isScrubbingLoading = action.payload;
    },
    setIsEditingNumber(state, action: PayloadAction<boolean>) {
      state.isEditingNumber = action.payload;
    },
    setPhoneNumber(state, action: PayloadAction<string>) {
      state.userDetails.phoneNumber = action.payload;
    },
    setScrubLocation(state, action: PayloadAction<Address | null>) {
      state.scrubLocation = action.payload;
    },
    setConfirmModal(state, action: PayloadAction<boolean>) {
      state.confirmModal = action.payload;
    },
    setOrderSucceeded(state, action: PayloadAction<boolean>) {
      state.didOrderSucceed = action.payload;
    },
    setIsEditingAddress(state, action: PayloadAction<boolean>) {
      state.isEditingAddress = action.payload;
    },
    setEditAddress(state, action: PayloadAction<Address>) {
      state.editAddress = action.payload;
    },
    setCurrentPage(state, action: PayloadAction<Pages>) {
      state.currentPage = action.payload;
    },
    setInvoiceHistoryLoading(state, action: PayloadAction<boolean>) {
      state.currentMonthlyBill.loading = action.payload;
    },
    setInvoiceHistory(state, action: PayloadAction<number | null | undefined>) {
      state.currentMonthlyBill.currentMonthlyBill = action.payload;
      state.currentMonthlyBill.loading = false;
    },
    setPersona(state, action: PayloadAction<Persona>) {
      state.persona = action.payload;
    },
    setOrderFail(state, action: PayloadAction<string | null>) {
      state.didOrderFail.failureReason = action.payload;
    },
    setOrderAttempts(state, action: PayloadAction<number>) {
      state.orderAttempts = action.payload;
    },
    addOrderAttempts(state) {
      state.orderAttempts = state.orderAttempts + 1;
    },
  },
});

export const {
  setConfirmModal,
  setCurrentPage,
  setEditAddress,
  setInvoiceHistory,
  setIsEditingAddress,
  setIsLoading,
  setIsOrderSubmitting,
  setIsScrubbingLoading,
  setOfferDetails,
  setOrderAttempts,
  addOrderAttempts,
  setOrderFail,
  setPersona,
  setPhoneNumber,
  setScrubLocation,
  setShippingAddress,
  setUserDetails,
  setIsEditingNumber,
  setStreamEligibility,
  getInvoiceHistory,
  getAllDetails,
  submitFullOrder,
  scrubAddress,
  setInvoiceHistoryLoading,
  setOfferDetailsLoading,
  setOrderSucceeded,
  getStreamEligibility,
  setStreamError,
  setConfigureStream,
  setConfigureStreamSuccess,
  setConfigureStreamError,
} = streamOnPromoSlice.actions;

export default streamOnPromoSlice.reducer;

export type SetErrorActionPayload = RuntimeError | ErrorsObj | undefined | null;

export const selectStreamOnPromo = (state: { streamOnPromo: StreamOnPromoState }) => state.streamOnPromo;
